import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader2, Phone, FlaskConical, Headphones, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import type { Lead } from '@fenix/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { leadsApi, scriptsApi, settingsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { fmtPhone } from '@/lib/format';

export function CallNowDialog({
  lead,
  onOpenChange,
}: {
  lead: Lead | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const open = !!lead;
  const settings = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get, enabled: open });
  const [language, setLanguage] = useState<string | undefined>();
  const [templateId, setTemplateId] = useState<string | undefined>();
  const [useMock, setUseMock] = useState(false);
  const lang = language ?? settings.data?.callLanguage;
  const scripts = useQuery({
    queryKey: ['scripts', lang],
    queryFn: () => scriptsApi.list(lang),
    enabled: open && !!lang,
  });
  const languages = useQuery({
    queryKey: ['languages'],
    queryFn: settingsApi.languages,
    enabled: open,
  });

  const mut = useMutation({
    mutationFn: () =>
      leadsApi.call(lead!.id, {
        language: lang,
        scriptTemplateId: templateId,
        useMock: useMock || undefined,
      }),
    onSuccess: (call) => {
      toast.success('Ligação iniciada');
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['calls'] });
      onOpenChange(false);
      navigate(`/chamadas/${call.id}`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha ao iniciar ligação'),
  });

  const webMut = useMutation({
    mutationFn: () =>
      leadsApi.webCall(lead!.id, {
        language: lang,
        scriptTemplateId: templateId,
        useMock: useMock || undefined,
      }),
    onSuccess: ({ call, accessToken }) => {
      toast.success('Ligação aberta no navegador');
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['calls'] });
      onOpenChange(false);
      navigate(`/chamadas/${call.id}`, { state: { accessToken } });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Falha ao abrir a ligação no navegador'),
  });

  const providerIsMock = settings.data?.voiceProvider === 'mock';
  const missingApiKey =
    settings.data && settings.data.voiceProvider === 'retell' && !settings.data.hasRetellApiKey;
  // A web call não passa por telefonia, então não exige número de origem.
  const notConfigured =
    missingApiKey || (settings.data?.voiceProvider === 'retell' && !settings.data.fromNumber);
  const noScripts = scripts.data?.length === 0;
  // Sem URL pública o Retell não entrega os webhooks: a ligação acontece, mas transcrição,
  // sentimento e resultado nunca chegam — e a chamada fica presa em "Chamando".
  const webhookUnreachable =
    settings.data?.voiceProvider === 'retell' &&
    /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:|\/|$)/.test(
      settings.data.webhookBaseUrl ?? '',
    );
  const invalidPhone = lead?.phoneValid === false;
  const busy = mut.isPending || webMut.isPending || invalidPhone;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ligar agora</DialogTitle>
          <DialogDescription>
            A IA atende como <strong>{lead?.name || 'o lead'}</strong> usando o script do idioma
            selecionado. <strong>Por telefone</strong> disca para{' '}
            <strong>{lead && fmtPhone(lead.phoneE164)}</strong>; <strong>no navegador</strong> você
            mesmo conversa com ela pelo microfone — mesma análise de sentimento, sem telefonia.
          </DialogDescription>
        </DialogHeader>

        {notConfigured && !useMock && (
          <Alert variant="destructive">
            <AlertDescription>
              {missingApiKey
                ? 'Configure a API key do Retell em Configurações antes de ligar.'
                : 'Falta o número de origem em Configurações para ligar por telefone. A ligação pelo navegador funciona mesmo assim.'}
            </AlertDescription>
          </Alert>
        )}
        {webhookUnreachable && !useMock && (
          <Alert>
            <AlertTriangle />
            <AlertDescription>
              A API está em <strong>{settings.data?.webhookBaseUrl}</strong>, que o Retell não
              alcança. A ligação funciona, mas transcrição, sentimento e resultado não voltam. Rode{' '}
              <code>ngrok http 3000</code>, coloque a URL em <code>PUBLIC_BASE_URL</code> e reinicie
              a API.
            </AlertDescription>
          </Alert>
        )}
        {lead?.phoneValid === false && (
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertDescription>
              O telefone <strong>{fmtPhone(lead.phoneE164)}</strong> não existe na numeração do
              país. Nem por telefone nem pelo navegador: corrija o número no cadastro do lead.
            </AlertDescription>
          </Alert>
        )}
        {lead?.product &&
          lead.product.active &&
          (!lead.product.offerText || !lead.product.checkoutUrl || !lead.product.couponCode) && (
            <Alert>
              <AlertTriangle />
              <AlertDescription>
                O produto <strong>{lead.product.name}</strong> está sem{' '}
                <strong>
                  {!lead.product.offerText
                    ? 'oferta'
                    : !lead.product.checkoutUrl
                      ? 'link de checkout'
                      : 'cupom'}
                </strong>
                : o piloto e as campanhas não ligam para este lead, porque a IA prometeria o que não
                chega. A ligação manual continua permitida para teste.
              </AlertDescription>
            </Alert>
          )}
        {lead?.product && !lead.product.active && (
          <Alert>
            <AlertTriangle />
            <AlertDescription>
              O produto <strong>{lead.product.name}</strong> está <strong>desativado</strong>: as
              campanhas não ligam para este lead. A ligação manual continua permitida — é o jeito de
              testar o produto antes de ativá-lo em Produtos.
            </AlertDescription>
          </Alert>
        )}
        {providerIsMock && (
          <Alert>
            <FlaskConical />
            <AlertDescription>
              O provedor atual é o <strong>simulador</strong>: nenhuma ligação real será feita.
              Troque para Retell em Configurações.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4">
          <div className="space-y-1.5">
            <Label>Idioma da ligação</Label>
            <Select
              value={lang}
              onValueChange={(v) => {
                setLanguage(v);
                setTemplateId(undefined);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.data?.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Script</Label>
            <Select
              value={
                templateId ?? scripts.data?.find((s) => s.isDefault)?.id ?? scripts.data?.[0]?.id
              }
              onValueChange={setTemplateId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Padrão do idioma" />
              </SelectTrigger>
              <SelectContent>
                {scripts.data?.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    {s.isDefault ? ' (padrão)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scripts.data && scripts.data.length === 0 && (
              <p className="text-xs text-destructive">
                Nenhum script para este idioma. Crie um em Scripts.
              </p>
            )}
          </div>
          {!providerIsMock && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label htmlFor="mock">Modo simulação</Label>
                <p className="text-xs text-muted-foreground">
                  Não liga de verdade; útil para testar o fluxo.
                </p>
              </div>
              <Switch id="mock" checked={useMock} onCheckedChange={setUseMock} />
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="secondary"
              onClick={() => webMut.mutate()}
              disabled={busy || (!!missingApiKey && !useMock) || noScripts}
              title="Fala com a IA pelo microfone do computador. Mesmo agente e mesma análise, sem telefonia."
            >
              {webMut.isPending ? <Loader2 className="animate-spin" /> : <Headphones />} Ligar no
              navegador
            </Button>
            <Button
              onClick={() => mut.mutate()}
              disabled={busy || (!!notConfigured && !useMock) || noScripts}
            >
              {mut.isPending ? <Loader2 className="animate-spin" /> : <Phone />} Ligar por telefone
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
